import React, { forwardRef, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import {
  useApiListJob,
  useApiFormJob,
  useApiListJobRun
} from '../../../../../api-new/state/auth'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { IconActivityLog, IconJob, IconReload } from '../../../../../shared/icon/icon'
import { FleetPage, FleetSection, EmptyState } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { formatAgo, formatDateTime, formatDuration, serverMessage } from '../../../../../shared/sso-workspace/format'
import { JOB_NAMES, JobStatusPill } from './provider-health'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

const jobName = job => JOB_NAMES[job.name] || job.name

// What running a job by hand does, said before it runs.
const RUN_EFFECT = {
  'credential-expiry': 'It emails anyone due a warning about an expiring credential, or one the identity provider no longer accepts. Nobody is warned twice for the same threshold or the same rejection.',
  'domain-reverify': 'It checks every domain\'s DNS record now, may move domains to at risk or deactivated, and emails their contacts.',
  'consent-request-expiry': 'It expires approval requests older than 7 days and tells each requester once.'
}

// Jobs (Task 21A/B) in the workspace's language (audit section 4): each
// scheduled job by name, what it does, its last run and "Run now" (said
// before it runs, since two of them send email); its run history, newest
// first; and beside them, where SSO alerts are routed now (Alert routing:
// a platform route, plus each organization's own SSO list).
export const ProviderJobList = () => {
  const roleSysProvider = useRoleSysProvider()
  const canRun = Boolean(roleSysProvider.accessUpdate)

  const apiListJob = useApiListJob()
  const apiFormJob = useApiFormJob()

  const [jobs, setJobs] = useState(undefined)
  const [selected, setSelected] = useState(null)
  const [historyKey, setHistoryKey] = useState(0)
  const [confirming, setConfirming] = useState(null)
  const [confirmError, confirmFailure, setConfirmError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')

  const load = async () => {
    try { setJobs((await apiListJob.get()) || []) } catch (err) { setJobs(null) }
  }

  const jobsRef = useRef()
  const runsRef = useRef()

  const handleRun = async () => {
    const job = confirming
    setBusy(true)
    setConfirmError(null)
    let result
    try {
      result = await apiFormJob.postArg(`/${job.name}/run`)
    } catch (err) {
      setConfirmError(serverMessage(err, `${jobName(job)} couldn't be started.`), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setConfirming(null)
    const outcome = result && result.status
    setStatus(outcome === 'skipped'
      ? `${jobName(job)} was already running elsewhere, so this run was skipped.`
      : `${jobName(job)} ran${outcome ? `: ${outcome}` : ''}. Its run is at the top of the history.`)
    setSelected(job.name)
    setHistoryKey(k => k + 1)
    await load()
  }

  // lastRun is nested (and null for a job that has never run): flatten what
  // the grid shows onto the row so its quick filter, sort and CSV export
  // work off real values instead of "[object Object]". `selected` lives in
  // the row data because AG Grid only restyles a row whose data changed.
  const rows = useMemo(
    () =>
      Array.isArray(jobs)
        ? jobs.map(j => ({
            ...j,
            id: j.name,
            job: jobName(j),
            status: (j.lastRun && j.lastRun.status) || 'never run',
            startedAt: (j.lastRun && j.lastRun.startedAt) || null,
            durationMs: j.lastRun && j.lastRun.durationMs != null ? j.lastRun.durationMs : null,
            error: (j.lastRun && j.lastRun.error) || '',
            selected: j.name === selected
          }))
        : jobs,
    [jobs, selected]
  )
  const gridList = useGridList(rows)

  const selectedJob = (jobs || []).find(j => j.name === selected)

  return (
    <BreadcrumbView parent={PROVIDER_SECTION_BREADCRUMB} Icon={IconJob} text="Jobs">
      <FleetPage
        tabs
        title="Jobs"
        purpose="The scheduled work that keeps single sign-on healthy: domain checks, credential warnings and approval requests. Run one now, or see how its runs went.">
        <div className="sw-columns">
          <div className="min-w-0">
            <FleetSection id="jobs" title="Scheduled jobs">
              {status && <p className="sw-status" role="status" aria-live="polite">{status}</p>}
              {jobs === null && <p className="text-danger small" role="alert">The scheduled jobs couldn't be loaded. Reload to try again.</p>}
              <FleetGrid flush title="schedule" tableRef={jobsRef} exportName="jobs">
                <TableAgClient
                  ref={jobsRef}
                  apiList={gridList}
                  onReady={load}
                  rowHeight={ROW_HEIGHT_TWO_LINE}
                  getRowClass={params => (params.data && params.data.selected ? 'sw-hl' : '')}
                  emptyState={{ Icon: IconJob, title: 'No jobs', message: 'No jobs are registered with the auth service.' }}>
                  <ColumnFormat field="job" header="Job" render={jobCell} stacked minWidth={220} flex={2} sortable />
                  <ColumnFormat field="status" header="Last run" render={lastRunCell} sortable />
                  <ColumnFormat field="startedAt" header="Started" render={agoCell} sortable />
                  <ColumnFormat field="durationMs" header="Took" render={durationCell} sortable />
                  <ColumnFormat field="error" header="Error" render={errorCell} sortable />
                  <ColumnMenu
                    menu={j =>
                      <Menu small>
                        <MenuItem Icon={IconActivityLog} text="history" pin show onClick={() => setSelected(j.name)} />
                        <MenuItem Icon={IconReload} text="run now" pin show={canRun} onClick={() => { setConfirmError(null); setConfirming(j) }} />
                      </Menu>}
                  />
                </TableAgClient>
              </FleetGrid>
            </FleetSection>

            <FleetSection id="history" title={selectedJob ? `Run history: ${jobName(selectedJob)}` : 'Run history'}
              lede={selectedJob ? 'Newest first. A run that found another copy already running is recorded as skipped.' : undefined}>
              {selected
                ? <JobRuns key={`${selected}:${historyKey}`} ref={runsRef} name={selected} />
                : <EmptyState>Choose History on a job to see its recent runs.</EmptyState>}
            </FleetSection>
          </div>

          <FleetSection id="recipients" title="Who hears about SSO alerts"
            lede="Single sign-on alerts follow Alert routing now: a platform route, plus each organization's own SSO list.">
            <Link className="sw-link" to="/admin/alert-routing/routes?category=sso">Who hears about SSO alerts: open Alert routes</Link>
          </FleetSection>
        </div>
      </FleetPage>

      <ConfirmDialog
        open={Boolean(confirming)}
        title={confirming ? `Run ${jobName(confirming).toLowerCase()} now?` : ''}
        body={confirming ? RUN_EFFECT[confirming.name] || 'It runs now, as it would on its schedule.' : ''}
        confirmText="Run now"
        busy={busy}
        error={confirmError}
        failure={confirmFailure}
        onConfirm={handleRun}
        onCancel={() => setConfirming(null)}
      />
    </BreadcrumbView>
  )
}

const jobCell = row =>
  row.data
    ? <span><span className="fw-semibold">{row.data.job}</span><span className="sw-cell-sub">{row.data.description}</span></span>
    : ''

const lastRunCell = row => (row.data ? <JobStatusPill status={row.data.lastRun && row.data.lastRun.status} /> : '')
const runStatusCell = row => (row.data ? <JobStatusPill status={row.data.status} /> : '')
const agoCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')
const durationCell = row => (row.value != null && row.value !== '' ? formatDuration(row.value) : '')
const errorCell = row => (row.value ? <span className="sw-tone-bad">{row.value}</span> : '')

// One job's recent runs (GET /job/:name/runs), newest first.
const JobRuns = forwardRef(({ name }, ref) => {
  const api = useApiListJobRun()
  const [runs, setRuns] = useState(undefined)

  const load = async () => {
    try { setRuns((await api.get(`${name}/runs?limit=20`)) || []) } catch (err) { setRuns(null) }
  }

  const gridList = useGridList(runs)

  return (
    <>
      {runs === null && <p className="text-danger small" role="alert">The run history couldn't be loaded. Reload to try again.</p>}
      <FleetGrid compact flush title="runs" tableRef={ref} exportName={`${name}-runs`}>
        {/* queryKey: the schedule grid above keeps the plain keys. */}
        <TableAgClient
          ref={ref}
          queryKey="runs"
          apiList={gridList}
          onReady={load}
          sortCol="startedAt"
          sortDesc
          emptyState={{ Icon: IconJob, title: 'No runs yet', message: "This job hasn't run yet." }}>
          <ColumnFormat field="status" header="Result" render={runStatusCell} sortable />
          <ColumnFormat field="startedAt" header="Started" render={agoCell} sortable />
          <ColumnFormat field="durationMs" header="Took" render={durationCell} sortable />
          <ColumnText field="host" header="Ran on" sortable />
          <ColumnFormat field="error" header="Error" render={errorCell} sortable />
        </TableAgClient>
      </FleetGrid>
    </>
  )
})
